// KioskScreen — the paired-device surface. Operator enters or scans a
// student ID, taps Record. Every capture is durably ENQUEUED to the
// outbox first, then the flusher attempts the network round-trip
// immediately. Outcomes:
//
//   ok / late / deduped → flushed inline; flash overlay shows the
//                          server's verdict, entry has been removed
//                          from the outbox.
//   queued              → flush failed (offline, 5xx, transient); the
//                          entry is durably persisted and will retry
//                          on the next flusher tick. UX is the same
//                          colour as `ok` — from the operator's POV
//                          the student is "recorded" the moment the
//                          outbox accepts the entry.
//   401                  → device token revoked; clear local state via
//                          onRevoked.
//   400/other            → flush failed but the failure is permanent
//                          (bad student ID, etc.); the kiosk surfaces
//                          the detail string AND removes the entry so
//                          we don't retry a bad capture forever.
//
// The flash overlay auto-clears so the next student is fast.

import {
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Button,
  Card,
  Field,
  Stack,
  tokens,
} from "@sms/design-system";
import type { Locale } from "@sms/i18n";
import { isApiError, UnauthorizedError } from "@sms/sdk";

import type { Flusher, FlushResult } from "./flusher";
import { makeT } from "./i18n";
import type { OutboxAPI } from "./outbox";
import { parseStudentIdFromQR, QRScanner } from "./QRScanner";

const FLASH_MS = 2000;

type Outcome =
  | { kind: "idle" }
  | { kind: "recording" }
  | { kind: "ok" }
  | { kind: "late" }
  | { kind: "deduped" }
  | { kind: "queued" }
  | { kind: "error"; message: string };

export interface KioskScreenProps {
  locale: Locale;
  device: { gate_id?: string; label: string; school_id: string };
  outbox: OutboxAPI;
  flusher: Pick<Flusher, "wake">;
  onUnpair: () => void;
  /** Surfaced when the server tells us the token is revoked. */
  onRevoked: () => void;
}

export function KioskScreen({
  locale,
  device,
  outbox,
  flusher,
  onUnpair,
  onRevoked,
}: KioskScreenProps) {
  const t = makeT(locale);
  const [studentId, setStudentId] = useState("");
  const [outcome, setOutcome] = useState<Outcome>({ kind: "idle" });
  const [queued, setQueued] = useState(outbox.size());
  const [scanning, setScanning] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Live counter — re-renders the badge when the outbox shrinks /
  // grows (either we enqueued here, or the background flusher tick
  // drained an entry).
  useEffect(() => outbox.subscribe(() => setQueued(outbox.size())), [outbox]);

  // Auto-clear the flash + restore focus so the next student is fast.
  useEffect(() => {
    if (outcome.kind === "idle" || outcome.kind === "recording") return;
    const t = setTimeout(() => {
      setOutcome({ kind: "idle" });
      setStudentId("");
      inputRef.current?.focus();
    }, FLASH_MS);
    return () => clearTimeout(t);
  }, [outcome.kind]);

  const recordCapture = useCallback(
    async (rawId: string, method: "qr" | "manual") => {
      const id = rawId.trim();
      if (!device.gate_id || !id) return;
      // Step 1: durably persist. Power-cycle can't lose it from here.
      const entry = outbox.enqueue({
        student_id: id,
        gate_id: device.gate_id,
        capture_method: method,
      });
      setOutcome({ kind: "recording" });
      let result: FlushResult;
      try {
        result = await flusher.wake();
      } catch {
        result = { kind: "skip", reason: "busy" };
      }
      if (result.kind === "ok") {
        if (result.deduped) setOutcome({ kind: "deduped" });
        else if (result.isLate) setOutcome({ kind: "late" });
        else setOutcome({ kind: "ok" });
        return;
      }
      if (result.kind === "fail") {
        if (result.error instanceof UnauthorizedError) {
          onRevoked();
          return;
        }
        if (
          isApiError(result.error) &&
          result.error.status < 500 &&
          result.error.status !== 401
        ) {
          outbox.remove(entry.id);
          const message =
            result.error.problem.detail ?? t("gate.kiosk_invalid_student");
          setOutcome({ kind: "error", message });
          return;
        }
        setOutcome({ kind: "queued" });
        return;
      }
      setOutcome({ kind: "queued" });
    },
    [device.gate_id, flusher, outbox, onRevoked, t],
  );

  const onScanned = useCallback(
    (raw: string) => {
      setScanning(false);
      const parsed = parseStudentIdFromQR(raw);
      setStudentId(parsed);
      void recordCapture(parsed, "qr");
    },
    [recordCapture],
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    await recordCapture(studentId, "manual");
  };

  return (
    <main style={pageStyle} data-testid="kiosk-root">
      <style>{kioskCSS}</style>
      <header style={headerStyle}>
        <Stack gap={1}>
          <p style={overlineStyle}>{t("gate.gate_label")}</p>
          <h1 style={titleStyle}>
            {t("gate.kiosk_title", { label: device.label })}
          </h1>
        </Stack>
        <Stack direction="row" gap={2} align="center">
          {queued > 0 && (
            <span
              data-testid="queued-badge"
              data-count={queued}
              style={queuedBadgeStyle}
              aria-label={t("gate.queued_label", { count: String(queued) })}
            >
              <span style={queuedDotStyle} aria-hidden="true" />
              {t("gate.queued_label", { count: String(queued) })}
            </span>
          )}
          <Button variant="ghost" onClick={onUnpair}>
            {t("gate.kiosk_unpair")}
          </Button>
        </Stack>
      </header>

      <p style={helpStyle}>{t("gate.kiosk_help")}</p>

      <Stack gap={4}>
        {scanning ? (
          <QRScanner
            onScan={onScanned}
            onClose={() => setScanning(false)}
            labels={{
              closeAction: t("gate.scan_close"),
              requestingCamera: t("gate.scan_requesting_camera"),
              permissionDenied: t("gate.scan_permission_denied"),
              unsupported: t("gate.scan_unsupported"),
            }}
          />
        ) : (
          <Button
            size="lg"
            onClick={() => setScanning(true)}
            disabled={outcome.kind === "recording" || !device.gate_id}
            data-testid="scan-toggle"
            fullWidth
          >
            {t("gate.scan_open")}
          </Button>
        )}

        <Card variant="elevated" padding={5}>
          <form onSubmit={submit}>
            <Stack gap={4}>
              <Field label={t("gate.kiosk_student_id")}>
                <input
                  ref={inputRef}
                  name="student-id"
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  spellCheck={false}
                  autoFocus
                  required
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                  className="ds-gate-input"
                />
              </Field>
              <Button
                type="submit"
                size="lg"
                fullWidth
                disabled={outcome.kind === "recording" || !device.gate_id}
              >
                {outcome.kind === "recording"
                  ? t("gate.kiosk_recording")
                  : t("gate.kiosk_record")}
              </Button>
            </Stack>
          </form>
        </Card>

        {outcome.kind !== "idle" && outcome.kind !== "recording" && (
          <FlashOverlay outcome={outcome} t={t} />
        )}
      </Stack>
    </main>
  );
}

function FlashOverlay({
  outcome,
  t,
}: {
  outcome: Exclude<Outcome, { kind: "idle" } | { kind: "recording" }>;
  t: (k: string, v?: Record<string, string>) => string;
}) {
  const { palette, label, glyph } = (() => {
    switch (outcome.kind) {
      case "ok":
        return {
          palette: { bg: tokens.color.success[50], fg: tokens.color.success[700] },
          label: t("gate.kiosk_recorded"),
          glyph: <CheckIcon />,
        };
      case "late":
        return {
          palette: { bg: tokens.color.warning[50], fg: tokens.color.warning[700] },
          label: t("gate.kiosk_late"),
          glyph: <ClockIcon />,
        };
      case "deduped":
        return {
          palette: {
            bg: tokens.color.semantic.bgSubtle,
            fg: tokens.color.semantic.textMuted,
          },
          label: t("gate.kiosk_deduped"),
          glyph: <DotIcon />,
        };
      case "queued":
        return {
          palette: { bg: tokens.color.info[50], fg: tokens.color.info[700] },
          label: t("gate.kiosk_queued"),
          glyph: <QueuedIcon />,
        };
      case "error":
        return {
          palette: { bg: tokens.color.danger[50], fg: tokens.color.danger[700] },
          label: outcome.message,
          glyph: <ErrorIcon />,
        };
    }
  })();
  const role = outcome.kind === "error" ? "alert" : "status";
  return (
    <div
      role={role}
      data-outcome={outcome.kind}
      style={{
        ...flashStyle,
        background: palette.bg,
        color: palette.fg,
      }}
    >
      <span aria-hidden="true" style={{ display: "flex" }}>
        {glyph}
      </span>
      <span style={flashLabelStyle}>{label}</span>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="32"
      height="32"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="32"
      height="32"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function DotIcon() {
  return (
    <svg viewBox="0 0 24 24" width="32" height="32">
      <circle cx="12" cy="12" r="4" fill="currentColor" />
    </svg>
  );
}

function QueuedIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="32"
      height="32"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12a9 9 0 1 1-3-6.7" />
      <path d="M21 4v6h-6" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="32"
      height="32"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5" />
      <path d="M12 17h.01" />
    </svg>
  );
}

// ── styles ───────────────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 720,
  margin: "0 auto",
  padding: tokens.space[6],
  minHeight: "100dvh",
  boxSizing: "border-box",
};
const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: tokens.space[3],
  marginBottom: tokens.space[3],
  flexWrap: "wrap",
};
const overlineStyle: React.CSSProperties = {
  fontSize: tokens.type.size.xs,
  fontWeight: tokens.type.weight.semibold,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: tokens.color.semantic.textMuted,
  margin: 0,
};
const titleStyle: React.CSSProperties = {
  fontSize: 28,
  fontWeight: tokens.type.weight.bold,
  color: tokens.color.semantic.heading,
  letterSpacing: "-0.02em",
  lineHeight: 1.2,
  margin: 0,
};
const helpStyle: React.CSSProperties = {
  fontSize: tokens.type.size.base,
  color: tokens.color.semantic.textMuted,
  marginBottom: tokens.space[5],
  marginTop: tokens.space[2],
};
const flashStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: tokens.space[3],
  padding: `${tokens.space[5]} ${tokens.space[6]}`,
  borderRadius: tokens.radius.lg,
  border: `1px solid currentColor`,
  // Soft drop-in feel; user perception is "the outcome just appeared".
  animation: `dsKioskFlashIn ${tokens.motion.durBase} ${tokens.motion.easeOut}`,
};
const flashLabelStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: tokens.type.weight.bold,
  letterSpacing: "-0.01em",
};
const queuedBadgeStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: tokens.space[1],
  background: tokens.color.warning[50],
  color: tokens.color.warning[700],
  padding: `${tokens.space[1]} ${tokens.space[3]}`,
  borderRadius: tokens.radius.pill,
  fontSize: tokens.type.size.sm,
  fontWeight: tokens.type.weight.semibold,
};
const queuedDotStyle: React.CSSProperties = {
  display: "inline-block",
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: tokens.color.warning[500],
  animation: "dsKioskPulse 1.6s ease-in-out infinite",
};

const kioskCSS = `
@keyframes dsKioskFlashIn {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes dsKioskPulse {
  0%, 100% { opacity: 0.5; }
  50%      { opacity: 1; }
}
/* Large gate-friendly student-id input. Pinned here rather than the
 * global baseline because the kiosk is operated standing up with the
 * device 2-3 feet away — the type needs to be readable from there. */
.ds-gate-input {
  width: 100%;
  padding: 18px 20px;
  font-size: 24px;
  font-family: inherit;
  letter-spacing: 0.04em;
  border: 1.5px solid ${tokens.color.semantic.border};
  border-radius: 12px;
  background: ${tokens.color.semantic.surface};
  box-sizing: border-box;
  color: ${tokens.color.semantic.text};
}
.ds-gate-input:focus-visible {
  outline: none;
  border-color: ${tokens.color.brand[500]};
  box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.12);
}
@media (prefers-reduced-motion: reduce) {
  [data-outcome] { animation: none !important; }
  [data-count] [aria-hidden] { animation: none !important; }
}
`;
